import { withStyles } from "@material-ui/core/styles";
import clsx from "clsx";
import React from "react";
import { Column, Table } from "react-virtualized";
import { Cell, HeadCell, IColumn, InputCell } from "./Cells";
import { tableStyles } from "./styles";

export interface VirtualizedTableProps {
  [property: string]: any;

  classes?: any;
  /** Altura da tabela. */
  height?: number;
  /** Largura da tabela. */
  width?: number;
  /** Configuração das colunas da tabela. */
  columns: Array<IColumn>;
  /** Total de linhas que serão renderizadas na tabela. */
  rowCount: number;
  /** Evento disparado ao renderezar as linhas em tela. Deve retornar os dados da linha. */
  rowGetter: Function;
  /** Define a altura das linhas. */
  rowHeight?: number;
  /** Habilita/Desabilita o hover sobre as linhas que altera a cor de fundo da linha. */
  rowHover?: boolean;
  /** Altura do cabeçalho. */
  headerHeight?: number;
  /** Classe da tabela. */
  tableClassName?: string;
  /** Classe das células do corpo da tabela. */
  cellClassName?: string;
  /** Classe das células do cabeçalho. */
  headCellClassName?: string;
  /** Função que será executada ao alterar o valor da célula e que deverá retornar o valor a ser apresentado no input.
   * Está função permite a converção de dados, como por exemplo, de texto para números. */
  onChangeInputCell?: (
    rowIndex: number | undefined,
    dataKey: string,
    value
  ) => any;
  /** Função que será executada ao clicar na célula o que habilitará o input de texto. */
  onEnableInputCell?: (rowIndex: number | undefined, dataKey: string) => any;
  /** Função que será executada ao alterar o valor do input, passando o novo valor digitado pelo usuário.
   * Por questão de performance, ficou definido que está função só será executada quando o usuário confirmar a alteração feita no input, ou seja, somente quando ele sair do campo ou apertar "Enter". */
  onChangeInputCellValue: (
    rowIndex: number | undefined,
    dataKey: string,
    value
  ) => void;
}

class MuiVirtualizedTable extends React.PureComponent<VirtualizedTableProps> {
  static defaultProps = {
    headerHeight: 48,
    rowHeight: 48,
    rowHover: true,
    height: 250,
    width: 100,
  };

  getColumnsWidth = (columns) => {
    return columns.reduce((sum: number, column) => sum + column.width, 0);
  };

  getRowClassName = ({ index }) => {
    const { classes, rowHover } = this.props;

    return clsx(classes.tableRow, classes.flexContainer, {
      [classes.tableRowHover]: index !== -1 && rowHover,
    });
  };

  cellRenderer = (props) => {
    const { cellClassName, rowHeight } = this.props;

    return !props.columnData.input ? (
      <Cell {...props} className={cellClassName} rowHeight={rowHeight}>
        {props.cellData}
      </Cell>
    ) : (
      <InputCell
        {...props}
        className={cellClassName}
        value={props.cellData}
        onChange={this.props.onChangeInputCell}
        onEnableInput={this.props.onEnableInputCell}
        onChangeInputValue={this.props.onChangeInputCellValue}
      />
    );
  };

  headerRenderer = (props) => {
    const { headCellClassName, headerHeight } = this.props;

    return (
      <HeadCell
        {...props}
        className={headCellClassName}
        headerHeight={headerHeight}
      >
        {props.label}
      </HeadCell>
    );
  };

  render() {
    const { classes, height, width, columns, tableClassName, ...tableProps } =
      this.props;
    const maxWidth = Math.max(width || 0, this.getColumnsWidth(columns));

    return (
      <Table
        height={height}
        width={maxWidth}
        gridStyle={{
          direction: "inherit",
        }}
        className={clsx(classes.table, tableClassName)}
        rowClassName={this.getRowClassName}
        /** Sobrepõe o valor inicial do tamanho da linha para assim permitir
         * o resize correto com a adição das colunas customizadas
         * @ignore */
        rowStyle={{ width: maxWidth }}
        overscanRowCount={10}
        {...tableProps}
      >
        {columns.map((column, index) => {
          return (
            <Column
              {...column}
              key={column.dataKey}
              className={classes.flexContainer}
              headerRenderer={(headerProps) =>
                this.headerRenderer({
                  ...headerProps,
                  columnIndex: index,
                  columnData: column,
                })
              }
              cellRenderer={(cellProps) =>
                this.cellRenderer({
                  ...cellProps,
                  columnData: column,
                })
              }
            />
          );
        })}
      </Table>
    );
  }
}

export default withStyles(tableStyles)(MuiVirtualizedTable);
